import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as S from '../facility-form/facility-form.styles';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { AddressType, CountryType, StateType, SupportFacility, CreateClaimFacility } from '../../../../api';
import * as CS from '../section.styles';
import { InputField, SelectField } from '../../../base/form';
import { BaseButton } from '../../../base';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { getOptions } from '../../../base/select';
import { validateFacilityFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';
import { facilityIdentityKey } from '../../../../stores/claim-editor/claim-editor-linked-ids';
import { useVerifiedLink } from '../use-verified-link';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface Props {
    claimEditorStore: ClaimEditorStore;
    serviceIndex: number;
}

interface FacilityFormValues {
    facilityLookup?: string;
    facilityId?: string;
    name: string;
    ein: string;
    npi: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: StateType;
    postalCode: string;
    country: CountryType;
}

/** The form values a canonical facility record fills in — from a lookup pick or a fetch by id. */
const valuesFromRecord = (facility: SupportFacility): FacilityFormValues => {
    const address = facility.addresses?.[0];
    return {
        facilityLookup: facility.name || '',
        facilityId: facility.id || '',
        name: facility.name || '',
        ein: facility.ein || '',
        npi: facility.npi || '',
        addressLine1: address?.lines?.[0] || '',
        addressLine2: address?.lines?.[1] || '',
        city: address?.city || '',
        state: address?.state || StateType.AL,
        postalCode: address?.postal || '',
        country: address?.country || CountryType.US,
    };
};

export const ServiceLineFacilityForm: React.FunctionComponent<Props> = observer(({ claimEditorStore, serviceIndex }) => {
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const formikRef = useRef<FormikProps<FacilityFormValues> | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(Boolean((claimEditorStore.services?.[serviceIndex] as any)?.facility));
    const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);

    const svc: any = (claimEditorStore.services || [])[serviceIndex] || {};
    const current = svc.facility as CreateClaimFacility | undefined;

    // The claim's own fields cannot vouch for its `facilityId`; see use-verified-link. The API
    // ignores every other facility field while the id is present, so it is sent only while
    // verified and the form still describes that record.
    const { setLink, resolveId, isPending, settled, verificationFailed, retry } = useVerifiedLink(
        current?.facilityId,
        (id: string) => claimEditorStore.getFacilityById(id),
        (record: SupportFacility) => facilityIdentityKey(valuesFromRecord(record)),
    );
    const effectiveFacilityId = (v: FacilityFormValues): string | undefined =>
        resolveId(v.facilityId, facilityIdentityKey(v));

    const getInitialValues = useCallback((): FacilityFormValues => {
        if (current) {
            const address = current.addresses?.[0];
            return {
                facilityLookup: current.name || '',
                facilityId: current.facilityId,
                name: current.name || '',
                ein: current.ein || '',
                npi: current.npi || '',
                addressLine1: address?.lines?.[0] || '',
                addressLine2: address?.lines?.[1] || '',
                city: address?.city || '',
                state: address?.state || StateType.AL,
                postalCode: address?.postal || '',
                country: address?.country || CountryType.US,
            };
        }
        return {
            facilityLookup: '',
            facilityId: undefined,
            name: '',
            ein: '',
            npi: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: StateType.AL,
            postalCode: '',
            country: CountryType.US,
        };
    }, [current]);

    const convertToFacility = (values: FacilityFormValues): CreateClaimFacility => {
        const lines = [values.addressLine1];
        if (values.addressLine2) lines.push(values.addressLine2);
        return {
            facilityId: effectiveFacilityId(values),
            name: values.name,
            ein: values.ein || undefined,
            npi: values.npi || undefined,
            addresses: [{ name: values.name, lines, city: values.city, state: values.state, postal: values.postalCode, country: values.country, type: AddressType.MAILING }],
        };
    };

    const debouncedSave = useCallback(
        _.debounce(() => {
            const latestValues = formikRef.current?.values;
            if (!latestValues) return;
            // Until the id is verified this could only write the record back WITHOUT it;
            // the `settled` effect below persists again once the answer is known.
            if (isPending()) return;
            claimEditorStore.updateServiceLineFacility(serviceIndex, convertToFacility(latestValues));
        }, 500),
        [claimEditorStore, serviceIndex],
    );

    useEffect(() => {
        if (settled) debouncedSave();
    }, [settled, debouncedSave]);

    const persistFacility = useCallback(() => {
        const latestValues = formikRef.current?.values;
        if (!latestValues) return;
        if (isPending()) return;
        claimEditorStore.updateServiceLineFacility(serviceIndex, convertToFacility(latestValues));
    }, [claimEditorStore, serviceIndex]);

    const handleSelect = useCallback((facility: SupportFacility, formik: FormikProps<FacilityFormValues>) => {
        const newValues = valuesFromRecord(facility);
        // The picked record IS canonical: link it before the save reads the id.
        if (facility.id) setLink({ id: facility.id, key: facilityIdentityKey(newValues) });
        formik.setValues(newValues);
        debouncedSave();
    }, [debouncedSave, setLink]);

    useEffect(() => {
        setIsEditing(Boolean((claimEditorStore.services?.[serviceIndex] as any)?.facility));
        return () => {
            debouncedSave.cancel();
            if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
        };
    }, [claimEditorStore.services, serviceIndex, debouncedSave]);

    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const fieldPrefix = `services[${serviceIndex}].facility`;
    const doSearch = useCallback(
        async (q: string) => {
            const query = q.trim();
            if (!query) {
                return (claimEditorStore as any).searchFacilities(query, undefined);
            }

            // UUID-ish values are treated as IDs first, then we fallback to name search.
            const looksLikeFacilityId = /^[a-f0-9-]{8,}$/i.test(query);
            if (looksLikeFacilityId) {
                return (claimEditorStore as any).searchFacilities(undefined, query);

            }

            return (claimEditorStore as any).searchFacilities(query, undefined);
        },
        [claimEditorStore],
    );

    return (
        <>
        {!isEditing ? (
            <CS.ButtonContainer>
                <BaseButton variant="primary" onClick={(): void => {
                    // create an empty facility object so service.facility exists immediately
                    claimEditorStore.updateServiceLineFacility(serviceIndex, {
                        name: '',
                        ein: '',
                        npi: '',
                        addresses: [],
                    } as CreateClaimFacility);
                    setIsEditing(true);
                }}>+ Add Facility</BaseButton>
            </CS.ButtonContainer>
        ) : null}

        {isEditing && (
        <Formik
            innerRef={formikRef as any}
            initialValues={getInitialValues()}
            validate={values =>
                // Validate the id that will actually be sent: once the fields diverge from a
                // (now disabled) facility, it is no longer referenced and must not block submit.
                validateFacilityFormValues({ ...values, facilityId: effectiveFacilityId(values) }, {
                    isFacilityIdActive: claimEditorStore.isFacilityIdActive,
                })
            }
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Auto-save on value change - save whenever values change, not just when dirty
                useEffect(() => {
                    debouncedSave();
                }, [formik.values, debouncedSave]);

                useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated, () => {
                    debouncedSave.cancel();
                    persistFacility();
                });

                return (
                <S.FormContainer>
                    {verificationFailed && (
                        <FormErrorBanner
                            messages={['Could not verify the linked facility record, so changes to the facility are not being saved.']}
                            onRetry={retry}
                        />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <BaseButton variant="warning" onClick={(): void => { claimEditorStore.deleteServiceLineFacility(serviceIndex); setIsEditing(false); }}>✕ Remove Facility</BaseButton>
                    </div>
                    <S.FormGrid>
                        <S.FormField span={2} id={fieldDomId(`${fieldPrefix}.facilityId`)}>
                            <S.Label>Facility Lookup</S.Label>
                            <Field
                                name="facilityLookup"
                                component={AsyncSelectField}
                                placeholder="Type facility name to search"
                                loadOptions={async (q: string) => {
                                    const results = await doSearch(q);
                                    return results.map((f: SupportFacility) => ({ 
                                        label: f.name || '', 
                                        value: f.id as any, 
                                        facility: f,
                                        info: (() => {
                                            const address = [
                                                f.addresses?.[0]?.lines?.[0],
                                                f.addresses?.[0]?.lines?.[1],
                                                f.addresses?.[0]?.lines?.[2],
                                                f.addresses?.[0]?.city,
                                                f.addresses?.[0]?.state,
                                                f.addresses?.[0]?.country,
                                                f.addresses?.[0]?.postal
                                            ]
                                                .filter(Boolean)
                                                .join(' ');
                                            return address ? ' | ' + address : undefined;
                                        })(),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const f: SupportFacility = opt?.facility;
                                    if (!f) {
                                        form.setFieldValue('facilityId', undefined);
                                        return;
                                    }
                                    if (f.id) {
                                        claimEditorStore.markFacilityIdActive(f.id);
                                    }
                                    const newValues = valuesFromRecord(f);
                                    // The picked record IS canonical: link it before the save reads the id.
                                    if (f.id) setLink({ id: f.id, key: facilityIdentityKey(newValues) });
                                    form.setValues(newValues);
                                    debouncedSave();
                                }}
                            />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.name`)}>
                            <S.Label required>Name</S.Label>
                            <Field name="name" placeholder="Facility Name" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].lines[0]`)}>
                            <S.Label required>Address Line 1</S.Label>
                            <Field name="addressLine1" placeholder="Address Line 1" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Address Line 2</S.Label>
                            <Field name="addressLine2" placeholder="Address Line 2" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.ein`)}>
                            <S.Label>EIN</S.Label>
                            <Field name="ein" placeholder="EIN" component={InputField} maxLength={9 as any} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.npi`)}>
                            <S.Label>NPI</S.Label>
                            <Field name="npi" placeholder="NPI" component={InputField} maxLength={10 as any} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].state`)}>
                            <S.Label required>State</S.Label>
                            <Field name="state" component={SelectField} options={stateOptions as any} placeholder="Select State" isClearable={false} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].city`)}>
                            <S.Label required>City</S.Label>
                            <Field name="city" placeholder="City" component={InputField} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].country`)}>
                            <S.Label required>Country</S.Label>
                            <Field name="country" component={SelectField} options={countryOptions as any} placeholder="Select Country" isClearable={false} />
                        </S.FormField>
                        <S.FormField id={fieldDomId(`${fieldPrefix}.addresses[0].postal`)}>
                            <S.Label required>Postal Code</S.Label>
                            <Field name="postalCode" placeholder="Postal Code" component={InputField} />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
        )}
        </>
    );
});

export default ServiceLineFacilityForm;

